import { useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { UserChip } from '@/components/ui/user-chip'
import { MultiButton, MultiButtonGroup, CompactMultiButton } from '@/components/ui/multi-button'
import type { MultiButtonVariant, MultiButtonSize } from '@/components/ui/multi-button'
import { DiagonalLines } from '@/components/ui/diagonal-lines'
import type { DiagonalLinesCorner } from '@/components/ui/diagonal-lines'
import { BrandLoadingBar } from '@/components/ui/brand-loading-bar'
import { Pencil, Trash2, Eye, Star, Download, Share2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { YoutubeEmbed } from '@/components/common/YoutubeEmbed'
import { SearchMultiSelect } from '@/components/common/SearchMultiSelect'
import { PublicNavbar } from '@/components/layout/PublicNavbar'
import { withErrorCode } from '@/lib/errors'

// ─── Navigation data ───────────────────────────────────────────────────────────

const NAV = [
  {
    tier: 'Foundation',
    items: [
      { id: 'colors', label: 'Colors' },
    ],
  },
  {
    tier: 'Atoms',
    items: [
      { id: 'button', label: 'Button' },
      { id: 'input', label: 'Input' },
      { id: 'label', label: 'Label' },
      { id: 'badge', label: 'Badge' },
      { id: 'separator', label: 'Separator' },
      { id: 'skeleton', label: 'Skeleton' },
      { id: 'avatar', label: 'Avatar' },
      { id: 'textarea', label: 'Textarea' },
      { id: 'brand-loading-bar', label: 'BrandLoadingBar' },
    ],
  },
  {
    tier: 'Molecules',
    items: [
      { id: 'card', label: 'Card' },
      { id: 'alert', label: 'Alert' },
      { id: 'select', label: 'Select' },
      { id: 'tabs', label: 'Tabs' },
      { id: 'dialog', label: 'Dialog' },
      { id: 'user-chip', label: 'UserChip' },
      { id: 'multi-button', label: 'MultiButton' },
      { id: 'compact-multi-button', label: 'CompactMultiButton' },
      { id: 'search-multi-select', label: 'SearchMultiSelect' },
      { id: 'diagonal-lines', label: 'DiagonalLines' },
    ],
  },
  {
    tier: 'Organisms',
    items: [
      { id: 'table', label: 'Table' },
      { id: 'form', label: 'Form' },
      { id: 'loading-spinner', label: 'LoadingSpinner' },
      { id: 'youtube-embed', label: 'YoutubeEmbed' },
      { id: 'page-header', label: 'PageHeader' },
    ],
  },
] as const

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

// ─── Shared helpers ────────────────────────────────────────────────────────────

function ShowcaseCard({
  id,
  title,
  description,
  children,
}: {
  id: string
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-8">
      <h3 className="mb-1 text-lg font-semibold">{title}</h3>
      <p className="mb-4 text-sm text-muted-foreground">{description}</p>
      <Card>
        <CardContent className="space-y-4 pt-6">{children}</CardContent>
      </Card>
    </section>
  )
}

function ControlRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly string[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      {options.map((opt) => (
        <Button
          key={opt}
          size="xs"
          variant={value === opt ? 'default' : 'outline'}
          onClick={() => onChange(opt)}
        >
          {opt}
        </Button>
      ))}
    </div>
  )
}

function CheckboxControl({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-20 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-primary"
      />
    </div>
  )
}

function Preview({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex min-h-[100px] items-center justify-center rounded-md border bg-muted/30 p-6',
        className
      )}
    >
      {children}
    </div>
  )
}

// ─── Foundation: Colors ───────────────────────────────────────────────────────

const BRAND_COLORS = [
  {
    name: 'brand-black',
    hex: '#171717',
    label: 'Black',
    usage: 'Body text, primary button, dark surfaces',
    textClass: 'text-brand-white',
  },
  {
    name: 'brand-white',
    hex: '#f2f2f3',
    label: 'White',
    usage: 'Page background, light surfaces, button text on dark',
    textClass: 'text-brand-black',
  },
  {
    name: 'brand-orange',
    hex: '#FF9D76',
    label: 'Orange',
    usage: 'Primary accent, focus rings, CTAs, chart-1',
    textClass: 'text-brand-black',
  },
  {
    name: 'brand-blue',
    hex: '#76F1FF',
    label: 'Blue',
    usage: 'Secondary highlight, chart-2',
    textClass: 'text-brand-black',
  },
  {
    name: 'brand-purple',
    hex: '#C176FF',
    label: 'Purple',
    usage: 'Tertiary highlight, chart-3',
    textClass: 'text-brand-black',
  },
  {
    name: 'brand-yellow',
    hex: '#FFE176',
    label: 'Yellow',
    usage: 'Warm highlight, chart-4',
    textClass: 'text-brand-black',
  },
] as const

const SEMANTIC_TOKENS = [
  {
    group: 'Surface',
    tokens: [
      { name: 'background', light: '#f2f2f3', dark: '#171717', usage: 'Page and layout background' },
      { name: 'foreground', light: '#171717', dark: '#f2f2f3', usage: 'Default text color' },
      { name: 'card', light: '#f2f2f3', dark: '#282828', usage: 'Card and popover fill' },
      { name: 'muted', light: '#e8e8e9', dark: '#272727', usage: 'Subtle section backgrounds' },
      { name: 'muted-foreground', light: '#7e7e7e', dark: '#919191', usage: 'Secondary / helper text' },
    ],
  },
  {
    group: 'Interactive',
    tokens: [
      { name: 'primary', light: '#171717', dark: '#f2f2f3', usage: 'Primary button fill' },
      { name: 'primary-foreground', light: '#f2f2f3', dark: '#171717', usage: 'Text on primary button' },
      { name: 'secondary', light: '#e3e3e4', dark: '#2b2b2b', usage: 'Secondary button fill' },
      { name: 'accent', light: '#FF9D76', dark: '#FF9D76', usage: 'Hover highlights and focus' },
      { name: 'ring', light: '#FF9D76', dark: '#FF9D76', usage: 'Keyboard focus ring' },
    ],
  },
  {
    group: 'Structure',
    tokens: [
      { name: 'border', light: '#d6d6d7', dark: '#3a3a3a', usage: 'Dividers and input borders' },
      { name: 'input', light: '#d6d6d7', dark: '#3a3a3a', usage: 'Input field border' },
      { name: 'destructive', light: '#e53e3e', dark: '#fc8181', usage: 'Error and delete actions' },
    ],
  },
] as const

function Swatch({
  hex,
  label,
  name,
  usage,
  textClass,
}: {
  hex: string
  label: string
  name: string
  usage: string
  textClass: string
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div
        className={cn('flex h-28 flex-col justify-end p-3', textClass)}
        style={{ backgroundColor: hex }}
      >
        <p className="text-sm font-semibold leading-tight">{label}</p>
        <p className="font-mono text-xs opacity-80">{hex}</p>
      </div>
      <div className="bg-card px-3 py-2">
        <p className="font-mono text-xs font-medium text-foreground">{name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{usage}</p>
      </div>
    </div>
  )
}

function SemanticRow({
  name,
  light,
  dark,
  usage,
}: {
  name: string
  light: string
  dark: string
  usage: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <div className="flex shrink-0 gap-1.5">
        <span
          title={`Light: ${light}`}
          className="block h-7 w-7 rounded border border-border"
          style={{ backgroundColor: light }}
        />
        <span
          title={`Dark: ${dark}`}
          className="block h-7 w-7 rounded border border-border"
          style={{ backgroundColor: dark }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-xs font-medium text-foreground">{name}</p>
        <p className="text-xs text-muted-foreground">{usage}</p>
      </div>
      <div className="hidden shrink-0 gap-2 sm:flex">
        <span className="font-mono text-xs text-muted-foreground">{light}</span>
        <span className="text-xs text-muted-foreground">/</span>
        <span className="font-mono text-xs text-muted-foreground">{dark}</span>
      </div>
    </div>
  )
}

function ColorGuideShowcase() {
  return (
    <section id="colors" className="scroll-mt-8 space-y-8">
      <div>
        <h3 className="mb-1 text-lg font-semibold">Brand Palette</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          The six brand colors. Use as Tailwind utilities:{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">bg-brand-orange</code>,{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">text-brand-blue</code>, etc.
          No other color scales are available.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {BRAND_COLORS.map((c) => (
            <Swatch key={c.name} {...c} />
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-lg font-semibold">Semantic Tokens</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Runtime CSS variables that adapt between light and dark mode. Left swatch = light, right = dark.
          Use as{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">bg-background</code>,{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">text-muted-foreground</code>, etc.
        </p>
        <div className="space-y-6">
          {SEMANTIC_TOKENS.map((group) => (
            <div key={group.group}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.group}
              </p>
              <div className="space-y-1.5">
                {group.tokens.map((t) => (
                  <SemanticRow key={t.name} {...t} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Atoms ────────────────────────────────────────────────────────────────────

type ButtonVariant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
type ButtonSize = 'default' | 'xs' | 'sm' | 'lg' | 'icon'

function ButtonShowcase() {
  const [variant, setVariant] = useState<ButtonVariant>('default')
  const [size, setSize] = useState<ButtonSize>('default')
  const [disabled, setDisabled] = useState(false)

  return (
    <ShowcaseCard
      id="button"
      title="Button"
      description="Primary interactive element. Use variant to control visual weight and size for hierarchy."
    >
      <ControlRow
        label="Variant"
        options={['default', 'destructive', 'outline', 'secondary', 'ghost', 'link']}
        value={variant}
        onChange={(v) => setVariant(v as ButtonVariant)}
      />
      <ControlRow
        label="Size"
        options={['default', 'xs', 'sm', 'lg', 'icon']}
        value={size}
        onChange={(v) => setSize(v as ButtonSize)}
      />
      <CheckboxControl label="Disabled" checked={disabled} onChange={setDisabled} />
      <Preview>
        <Button variant={variant} size={size} disabled={disabled}>
          {size === 'icon' ? '✓' : 'Click me'}
        </Button>
      </Preview>
    </ShowcaseCard>
  )
}

type InputState = 'default' | 'disabled' | 'error'

function InputShowcase() {
  const [state, setState] = useState<InputState>('default')

  return (
    <ShowcaseCard
      id="input"
      title="Input"
      description="Single-line text field. Used in forms for names, emails, search, and other short values."
    >
      <ControlRow
        label="State"
        options={['default', 'disabled', 'error']}
        value={state}
        onChange={(v) => setState(v as InputState)}
      />
      <Preview>
        <Input
          className={cn(
            'max-w-sm',
            state === 'error' &&
              'border-destructive ring-destructive/20 focus-visible:ring-destructive/20'
          )}
          placeholder="Type something…"
          disabled={state === 'disabled'}
          aria-invalid={state === 'error'}
        />
      </Preview>
    </ShowcaseCard>
  )
}

function LabelShowcase() {
  return (
    <ShowcaseCard
      id="label"
      title="Label"
      description="Accessible form label that associates descriptive text with a form control."
    >
      <Preview>
        <div className="w-full max-w-xs space-y-2">
          <Label htmlFor="label-demo-input">Email address</Label>
          <Input id="label-demo-input" type="email" placeholder="you@example.com" />
        </div>
      </Preview>
    </ShowcaseCard>
  )
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link'

function BadgeShowcase() {
  const [variant, setVariant] = useState<BadgeVariant>('default')

  return (
    <ShowcaseCard
      id="badge"
      title="Badge"
      description="Small status indicator used for roles, tags, and published/draft states."
    >
      <ControlRow
        label="Variant"
        options={['default', 'secondary', 'destructive', 'outline', 'ghost', 'link']}
        value={variant}
        onChange={(v) => setVariant(v as BadgeVariant)}
      />
      <Preview className="gap-2">
        <Badge variant={variant}>Published</Badge>
        <Badge variant={variant}>Dancer</Badge>
        <Badge variant={variant}>Season 3</Badge>
      </Preview>
    </ShowcaseCard>
  )
}

type SepOrientation = 'horizontal' | 'vertical'

function SeparatorShowcase() {
  const [orientation, setOrientation] = useState<SepOrientation>('horizontal')

  return (
    <ShowcaseCard
      id="separator"
      title="Separator"
      description="Visual divider for grouping related content sections."
    >
      <ControlRow
        label="Orientation"
        options={['horizontal', 'vertical']}
        value={orientation}
        onChange={(v) => setOrientation(v as SepOrientation)}
      />
      <Preview>
        {orientation === 'horizontal' ? (
          <div className="w-full max-w-xs space-y-3">
            <p className="text-sm">Section A</p>
            <Separator orientation="horizontal" />
            <p className="text-sm">Section B</p>
          </div>
        ) : (
          <div className="flex h-20 items-center gap-4">
            <p className="text-sm">Left</p>
            <Separator orientation="vertical" />
            <p className="text-sm">Right</p>
          </div>
        )}
      </Preview>
    </ShowcaseCard>
  )
}

type SkeletonShape = 'line' | 'circle' | 'block'

function SkeletonShowcase() {
  const [shape, setShape] = useState<SkeletonShape>('line')

  return (
    <ShowcaseCard
      id="skeleton"
      title="Skeleton"
      description="Loading placeholder that mimics the shape of the content it represents."
    >
      <ControlRow
        label="Shape"
        options={['line', 'circle', 'block']}
        value={shape}
        onChange={(v) => setShape(v as SkeletonShape)}
      />
      <Preview>
        {shape === 'line' && (
          <div className="w-full max-w-xs space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        )}
        {shape === 'circle' && <Skeleton className="h-16 w-16 rounded-full" />}
        {shape === 'block' && <Skeleton className="h-24 w-48 rounded-md" />}
      </Preview>
    </ShowcaseCard>
  )
}

type AvatarMode = 'initials' | 'image'

const MOCK_AVATAR_URL = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#6366f1"/><text x="50%" y="55%" dominant-baseline="central" text-anchor="middle" fill="white" font-size="16" font-family="sans-serif">JD</text></svg>'
)}`

function AvatarShowcase() {
  const [mode, setMode] = useState<AvatarMode>('initials')

  return (
    <ShowcaseCard
      id="avatar"
      title="Avatar"
      description="User profile image with an initials fallback when no photo is available."
    >
      <ControlRow
        label="Mode"
        options={['initials', 'image']}
        value={mode}
        onChange={(v) => setMode(v as AvatarMode)}
      />
      <Preview className="gap-4">
        <Avatar>
          {mode === 'image' && <AvatarImage src={MOCK_AVATAR_URL} alt="Jane Doe" />}
          <AvatarFallback>JD</AvatarFallback>
        </Avatar>
        <div className="text-left">
          <p className="text-sm font-medium">Jane Doe</p>
          <p className="text-xs text-muted-foreground">
            {mode === 'image' ? 'Using AvatarImage' : 'Initials fallback'}
          </p>
        </div>
      </Preview>
    </ShowcaseCard>
  )
}

function TextareaShowcase() {
  const [rows, setRows] = useState('3')
  const [disabled, setDisabled] = useState(false)

  return (
    <ShowcaseCard
      id="textarea"
      title="Textarea"
      description="Multi-line text field for bios, descriptions, and long-form content."
    >
      <ControlRow
        label="Rows"
        options={['2', '3', '5', '8']}
        value={rows}
        onChange={setRows}
      />
      <CheckboxControl label="Disabled" checked={disabled} onChange={setDisabled} />
      <Preview>
        <Textarea
          rows={parseInt(rows, 10)}
          disabled={disabled}
          placeholder="Write something…"
          className="max-w-sm"
        />
      </Preview>
    </ShowcaseCard>
  )
}

function BrandLoadingBarShowcase() {
  const [height, setHeight] = useState<'sm' | 'md' | 'lg'>('md')
  const [mode, setMode] = useState<'indeterminate' | 'determinate'>('indeterminate')
  const [value, setValue] = useState('60')

  return (
    <ShowcaseCard
      id="brand-loading-bar"
      title="BrandLoadingBar"
      description="A progress/loading bar using a sweeping gradient of all four brand accent colors (orange → yellow → purple → blue). Supports indeterminate (animated sweep) and determinate (fill) modes."
    >
      <ControlRow
        label="Mode"
        options={['indeterminate', 'determinate']}
        value={mode}
        onChange={(v) => setMode(v as typeof mode)}
      />
      <ControlRow
        label="Height"
        options={['sm', 'md', 'lg']}
        value={height}
        onChange={(v) => setHeight(v as typeof height)}
      />
      {mode === 'determinate' && (
        <ControlRow
          label="Value"
          options={['0', '25', '50', '75', '100']}
          value={value}
          onChange={setValue}
        />
      )}
      <Preview>
        <div className="w-full max-w-sm space-y-4">
          <BrandLoadingBar
            height={height}
            value={mode === 'determinate' ? parseInt(value, 10) : undefined}
          />
        </div>
      </Preview>
    </ShowcaseCard>
  )
}

// ─── Molecules ────────────────────────────────────────────────────────────────

function CardShowcase() {
  const [showHeader, setShowHeader] = useState(true)
  const [showFooter, setShowFooter] = useState(true)

  return (
    <ShowcaseCard
      id="card"
      title="Card"
      description="Content container with optional header (title + description) and footer sections."
    >
      <CheckboxControl label="Header" checked={showHeader} onChange={setShowHeader} />
      <CheckboxControl label="Footer" checked={showFooter} onChange={setShowFooter} />
      <Preview>
        <Card className="w-full max-w-sm">
          {showHeader && (
            <CardHeader>
              <CardTitle>Card Title</CardTitle>
              <CardDescription>Supporting description text goes here.</CardDescription>
            </CardHeader>
          )}
          <CardContent className={showHeader ? '' : 'pt-6'}>
            <p className="text-sm text-muted-foreground">
              This is the main content area of the card.
            </p>
          </CardContent>
          {showFooter && (
            <CardFooter>
              <Button size="sm">Action</Button>
              <Button size="sm" variant="ghost" className="ml-2">
                Cancel
              </Button>
            </CardFooter>
          )}
        </Card>
      </Preview>
    </ShowcaseCard>
  )
}

type AlertVariant = 'default' | 'destructive'

function AlertShowcase() {
  const [variant, setVariant] = useState<AlertVariant>('default')

  return (
    <ShowcaseCard
      id="alert"
      title="Alert"
      description="Inline message for feedback, warnings, and error states."
    >
      <ControlRow
        label="Variant"
        options={['default', 'destructive']}
        value={variant}
        onChange={(v) => setVariant(v as AlertVariant)}
      />
      <Preview>
        <Alert variant={variant} className="w-full max-w-sm">
          <AlertTitle>{variant === 'destructive' ? 'Error' : 'Heads up!'}</AlertTitle>
          <AlertDescription>
            {variant === 'destructive'
              ? withErrorCode('Something went wrong. Please try again.', 'app/showcase-error')
              : 'You can change this setting at any time in your profile.'}
          </AlertDescription>
        </Alert>
      </Preview>
    </ShowcaseCard>
  )
}

type SelectSize = 'default' | 'sm'

function SelectShowcase() {
  const [size, setSize] = useState<SelectSize>('default')

  return (
    <ShowcaseCard
      id="select"
      title="Select"
      description="Dropdown for choosing one option from a list. Used in admin filter controls."
    >
      <ControlRow
        label="Size"
        options={['default', 'sm']}
        value={size}
        onChange={(v) => setSize(v as SelectSize)}
      />
      <Preview>
        <Select>
          <SelectTrigger size={size} className="w-48">
            <SelectValue placeholder="Pick a role…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="choreographer">Choreographer</SelectItem>
            <SelectItem value="dancer">Dancer</SelectItem>
            <SelectItem value="videographer">Videographer</SelectItem>
            <SelectItem value="photographer">Photographer</SelectItem>
          </SelectContent>
        </Select>
      </Preview>
    </ShowcaseCard>
  )
}

type TabsVariant = 'default' | 'line'

function TabsShowcase() {
  const [variant, setVariant] = useState<TabsVariant>('default')

  return (
    <ShowcaseCard
      id="tabs"
      title="Tabs"
      description="Tabbed interface for switching between related content panels."
    >
      <ControlRow
        label="Variant"
        options={['default', 'line']}
        value={variant}
        onChange={(v) => setVariant(v as TabsVariant)}
      />
      <Preview>
        <Tabs defaultValue="overview" className="w-full max-w-sm">
          <TabsList variant={variant}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <p className="pt-2 text-sm text-muted-foreground">Overview content here.</p>
          </TabsContent>
          <TabsContent value="details">
            <p className="pt-2 text-sm text-muted-foreground">Details content here.</p>
          </TabsContent>
          <TabsContent value="settings">
            <p className="pt-2 text-sm text-muted-foreground">Settings content here.</p>
          </TabsContent>
        </Tabs>
      </Preview>
    </ShowcaseCard>
  )
}

function DialogShowcase() {
  return (
    <ShowcaseCard
      id="dialog"
      title="Dialog"
      description="Modal overlay for confirmations, forms, and focused interactions."
    >
      <Preview>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline">Open Dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Action</DialogTitle>
              <DialogDescription>
                This action cannot be undone. Are you sure you want to continue?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" size="sm">
                Cancel
              </Button>
              <Button variant="destructive" size="sm">
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Preview>
    </ShowcaseCard>
  )
}

type UserChipSize = 'sm' | 'md' | 'lg'
type UserChipVariant = 'pill' | 'inline'

const MOCK_USER = {
  displayName: 'Jane Doe',
  email: 'jane@example.com',
  photoURL: null,
}

function UserChipShowcase() {
  const [size, setSize] = useState<UserChipSize>('md')
  const [variant, setVariant] = useState<UserChipVariant>('pill')

  return (
    <ShowcaseCard
      id="user-chip"
      title="UserChip"
      description="Displays a user's avatar and name. Supports pill and inline layout modes."
    >
      <ControlRow
        label="Size"
        options={['sm', 'md', 'lg']}
        value={size}
        onChange={(v) => setSize(v as UserChipSize)}
      />
      <ControlRow
        label="Variant"
        options={['pill', 'inline']}
        value={variant}
        onChange={(v) => setVariant(v as UserChipVariant)}
      />
      <Preview>
        <UserChip user={MOCK_USER} size={size} variant={variant} />
      </Preview>
    </ShowcaseCard>
  )
}

const MULTI_BUTTON_ITEMS_EDIT = [
  { id: 'view',   icon: Eye,    label: 'View'   },
  { id: 'edit',   icon: Pencil, label: 'Edit'   },
  { id: 'delete', icon: Trash2, label: 'Delete', className: 'text-destructive hover:text-destructive' },
]

const MULTI_BUTTON_ITEMS_SHARE = [
  { id: 'star',     icon: Star,     label: 'Star'     },
  { id: 'share',    icon: Share2,   label: 'Share'    },
  { id: 'download', icon: Download, label: 'Download' },
]

function MultiButtonShowcase() {
  const [variant, setVariant] = useState<MultiButtonVariant>('default')
  const [size, setSize] = useState<MultiButtonSize>('md')

  return (
    <ShowcaseCard
      id="multi-button"
      title="MultiButton"
      description="Compact pill that reveals labelled actions on hover. Wrap rows in MultiButtonGroup to synchronise widths across a table column."
    >
      <ControlRow
        label="Variant"
        options={['default', 'outline', 'secondary', 'ghost']}
        value={variant}
        onChange={(v) => setVariant(v as MultiButtonVariant)}
      />
      <ControlRow
        label="Size"
        options={['sm', 'md', 'lg']}
        value={size}
        onChange={(v) => setSize(v as MultiButtonSize)}
      />
      <Preview className="flex-col gap-4">
        <MultiButton items={MULTI_BUTTON_ITEMS_EDIT} variant={variant} size={size} />
        <MultiButton items={MULTI_BUTTON_ITEMS_SHARE} variant={variant} size={size} />
      </Preview>
      <p className="text-xs text-muted-foreground">
        Hover (or tap) an icon to expand its label. Use{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono">MultiButtonGroup</code>{' '}
        to auto-sync widths across rows.
      </p>
      <Preview className="flex-col items-start gap-2">
        <p className="text-xs font-medium text-muted-foreground">MultiButtonGroup (synced widths)</p>
        <MultiButtonGroup>
          <div className="flex flex-col gap-2">
            {['Alice Chen', 'Bob Martin', 'Carol Reyes'].map((name) => (
              <div key={name} className="flex items-center justify-between gap-4 w-full max-w-xs">
                <span className="text-sm">{name}</span>
                <MultiButton
                  items={MULTI_BUTTON_ITEMS_EDIT}
                  variant={variant}
                  size={size}
                />
              </div>
            ))}
          </div>
        </MultiButtonGroup>
      </Preview>
    </ShowcaseCard>
  )
}

// ─── Molecules: CompactMultiButton ───────────────────────────────────────────

function CompactMultiButtonShowcase() {
  const [variant, setVariant] = useState<MultiButtonVariant>('default')
  const [size, setSize] = useState<MultiButtonSize>('md')
  const [selectedEdit, setSelectedEdit] = useState('view')
  const [selectedShare, setSelectedShare] = useState('star')

  return (
    <ShowcaseCard
      id="compact-multi-button"
      title="CompactMultiButton"
      description="Shows only the selected action at rest. Expands into the full multi-button layout on hover (desktop) or first tap (mobile)."
    >
      <ControlRow
        label="Variant"
        options={['default', 'outline', 'secondary', 'ghost']}
        value={variant}
        onChange={(v) => setVariant(v as MultiButtonVariant)}
      />
      <ControlRow
        label="Size"
        options={['sm', 'md', 'lg']}
        value={size}
        onChange={(v) => setSize(v as MultiButtonSize)}
      />
      <Preview className="flex-col gap-4">
        <p className="text-xs font-medium text-muted-foreground">Standard (labels on hover)</p>
        <CompactMultiButton
          items={MULTI_BUTTON_ITEMS_EDIT.map(item => ({
            ...item,
            onClick: () => setSelectedEdit(item.id),
          }))}
          selectedId={selectedEdit}
          variant={variant}
          size={size}
        />
        <CompactMultiButton
          items={MULTI_BUTTON_ITEMS_SHARE.map(item => ({
            ...item,
            onClick: () => setSelectedShare(item.id),
          }))}
          selectedId={selectedShare}
          variant={variant}
          size={size}
        />
      </Preview>
      <p className="text-xs text-muted-foreground">
        Click an action to change which item is shown at rest. Hover (or tap) to expand all options.
      </p>
      <Preview className="flex-col gap-4">
        <p className="text-xs font-medium text-muted-foreground">Icon only (no labels)</p>
        <CompactMultiButton
          iconOnly
          items={MULTI_BUTTON_ITEMS_EDIT.map(item => ({
            ...item,
            onClick: () => setSelectedEdit(item.id),
          }))}
          selectedId={selectedEdit}
          variant={variant}
          size={size}
        />
        <CompactMultiButton
          iconOnly
          items={MULTI_BUTTON_ITEMS_SHARE.map(item => ({
            ...item,
            onClick: () => setSelectedShare(item.id),
          }))}
          selectedId={selectedShare}
          variant={variant}
          size={size}
        />
      </Preview>
      <p className="text-xs text-muted-foreground">
        Expands to an icon-only row — no labels, no per-button expansion. On mobile the first tap expands; subsequent taps fire immediately.
      </p>
    </ShowcaseCard>
  )
}

// ─── Molecules: SearchMultiSelect ────────────────────────────────────────────

const MOCK_ROLE_ITEMS = [
  { id: 'Choreographer', label: 'Choreographer' },
  { id: 'Dancer', label: 'Dancer' },
  { id: 'Videographer', label: 'Videographer' },
  { id: 'Photographer', label: 'Photographer' },
  { id: 'Editor', label: 'Editor' },
]

const MOCK_MEMBER_ITEMS = [
  { id: 'alice', label: 'Alice Chen', avatarUrl: '' },
  { id: 'bob', label: 'Bob Martin', avatarUrl: '' },
  { id: 'carol', label: 'Carol Reyes', avatarUrl: '' },
  { id: 'dan', label: 'Dan Lee', avatarUrl: '' },
]

function SearchMultiSelectShowcase() {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])

  return (
    <ShowcaseCard
      id="search-multi-select"
      title="SearchMultiSelect"
      description="Search-driven multi-select with removable chips. Omit avatarUrl on items for text-only chips (roles/tags); include it for people pickers."
    >
      <Preview className="flex-col items-stretch gap-6 min-h-[160px]">
        <div className="w-full max-w-sm">
          <SearchMultiSelect
            label="Roles (text-only)"
            items={MOCK_ROLE_ITEMS}
            selected={selectedRoles}
            onChange={setSelectedRoles}
          />
        </div>
        <div className="w-full max-w-sm">
          <SearchMultiSelect
            label="Members (with avatars)"
            items={MOCK_MEMBER_ITEMS}
            selected={selectedMembers}
            onChange={setSelectedMembers}
          />
        </div>
      </Preview>
    </ShowcaseCard>
  )
}

// ─── Molecules: DiagonalLines ─────────────────────────────────────────────────

const DIAG_COLOR_OPTIONS = [
  { label: 'Current', value: 'currentColor' },
  { label: 'Orange',  value: 'var(--color-brand-orange)'  },
  { label: 'Blue',    value: 'var(--color-brand-blue)'    },
  { label: 'Purple',  value: 'var(--color-brand-purple)'  },
  { label: 'Yellow',  value: 'var(--color-brand-yellow)'  },
] as const

const DIAG_CORNER_OPTIONS: { label: string; value: DiagonalLinesCorner }[] = [
  { label: 'TL', value: 'top-left'     },
  { label: 'TR', value: 'top-right'    },
  { label: 'BL', value: 'bottom-left'  },
  { label: 'BR', value: 'bottom-right' },
]

function DiagonalLinesShowcase() {
  const [angle, setAngle]           = useState(24)
  const [spacing, setSpacing]       = useState(12)
  const [strokeWidth, setStroke]    = useState(1)
  const [colorLabel, setColorLabel] = useState<string>('Orange')
  const [lineCountLabel, setLineCountLabel] = useState<string>('Auto')
  const [cornerLabel, setCornerLabel]       = useState<string>('TL')

  const color      = DIAG_COLOR_OPTIONS.find((c) => c.label === colorLabel)?.value ?? 'currentColor'
  const lineCount  = lineCountLabel === 'Auto' ? undefined : Number(lineCountLabel)
  const corner     = DIAG_CORNER_OPTIONS.find((c) => c.label === cornerLabel)?.value ?? 'top-left'

  return (
    <ShowcaseCard
      id="diagonal-lines"
      title="DiagonalLines"
      description="Brand-trademark repeating diagonal line pattern. Use standalone as a decorative fill, or as an absolute overlay to mask the pattern to any element shape."
    >
      <ControlRow
        label="Angle °"
        options={['12', '24', '45', '60', '80']}
        value={String(angle)}
        onChange={(v) => setAngle(Number(v))}
      />
      <ControlRow
        label="Spacing"
        options={['6', '12', '20', '32']}
        value={String(spacing)}
        onChange={(v) => setSpacing(Number(v))}
      />
      <ControlRow
        label="Stroke"
        options={['0.5', '1', '2', '3']}
        value={String(strokeWidth)}
        onChange={(v) => setStroke(Number(v))}
      />
      <ControlRow
        label="Color"
        options={DIAG_COLOR_OPTIONS.map((c) => c.label)}
        value={colorLabel}
        onChange={setColorLabel}
      />
      <ControlRow
        label="Lines"
        options={['Auto', '5', '8', '12', '20']}
        value={lineCountLabel}
        onChange={setLineCountLabel}
      />
      <ControlRow
        label="Corner"
        options={DIAG_CORNER_OPTIONS.map((c) => c.label)}
        value={cornerLabel}
        onChange={setCornerLabel}
      />

      {/* Standalone fill */}
      <Preview>
        <div className="h-32 w-full overflow-hidden rounded-md border">
          <DiagonalLines
            angle={angle}
            spacing={spacing}
            lineCount={lineCount}
            strokeWidth={strokeWidth}
            color={color}
            corner={corner}
          />
        </div>
      </Preview>

      {/* Overlay examples */}
      <p className="text-xs font-medium text-muted-foreground">Masked overlays</p>
      <div className="grid grid-cols-3 gap-4">
        {/* Orange brand fill */}
        <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-xl bg-brand-orange">
          <DiagonalLines
            className="absolute inset-0 opacity-30"
            angle={angle}
            spacing={spacing}
            lineCount={lineCount}
            strokeWidth={strokeWidth}
            color="white"
            corner={corner}
          />
          <span className="relative z-10 text-sm font-semibold text-white">Overlay</span>
        </div>
        {/* Dark surface */}
        <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-xl bg-brand-black">
          <DiagonalLines
            className="absolute inset-0 opacity-25"
            angle={angle}
            spacing={spacing}
            lineCount={lineCount}
            strokeWidth={strokeWidth}
            color={color}
            corner={corner}
          />
          <span className="relative z-10 text-sm font-semibold text-brand-white">Dark</span>
        </div>
        {/* Standalone on muted */}
        <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-xl bg-muted">
          <DiagonalLines
            className="absolute inset-0"
            angle={angle}
            spacing={spacing}
            lineCount={lineCount}
            strokeWidth={strokeWidth}
            color={color}
            opacity={0.4}
            corner={corner}
          />
          <span className="relative z-10 text-sm font-semibold">Muted</span>
        </div>
      </div>
    </ShowcaseCard>
  )
}

// ─── Organisms ────────────────────────────────────────────────────────────────

const SAMPLE_ROWS = [
  { id: '1', name: 'Alice Chen', role: 'Choreographer', status: 'Published' },
  { id: '2', name: 'Bob Martin', role: 'Dancer', status: 'Draft' },
  { id: '3', name: 'Carol Reyes', role: 'Videographer', status: 'Published' },
]

function TableShowcase() {
  return (
    <ShowcaseCard
      id="table"
      title="Table"
      description="Structured data layout with column headers and row actions. Used in all admin list pages."
    >
      <Preview className="items-start overflow-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SAMPLE_ROWS.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.role}</TableCell>
                <TableCell>
                  <Badge variant={row.status === 'Published' ? 'default' : 'outline'}>
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="xs">
                    Edit
                  </Button>
                  <Button variant="ghost" size="xs" className="text-destructive">
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Preview>
    </ShowcaseCard>
  )
}

function FormShowcase() {
  return (
    <ShowcaseCard
      id="form"
      title="Form Pattern"
      description="Label + Input + Textarea + Button composition — the fundamental building block of all data entry."
    >
      <Preview className="items-start">
        <form className="w-full max-w-sm space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-1">
            <Label htmlFor="form-showcase-name">Name</Label>
            <Input id="form-showcase-name" placeholder="Jane Doe" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="form-showcase-email">Email</Label>
            <Input id="form-showcase-email" type="email" placeholder="jane@example.com" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="form-showcase-bio">Bio</Label>
            <Textarea id="form-showcase-bio" rows={3} placeholder="Tell us about yourself…" />
          </div>
          <div className="flex gap-2">
            <Button type="submit">Save</Button>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </div>
        </form>
      </Preview>
    </ShowcaseCard>
  )
}

function LoadingSpinnerShowcase() {
  return (
    <ShowcaseCard
      id="loading-spinner"
      title="LoadingSpinner"
      description="Full-screen loading state shown while async operations (auth, route guards) are in progress."
    >
      <Preview className="h-40">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </Preview>
    </ShowcaseCard>
  )
}

function YoutubeEmbedShowcase() {
  return (
    <ShowcaseCard
      id="youtube-embed"
      title="YoutubeEmbed"
      description="Responsive 16:9 YouTube video embed with rounded corners. Used on film detail pages."
    >
      <div className="mx-auto max-w-sm">
        <YoutubeEmbed youtubeId="jNQXAC9IVRw" title="YouTube Embed Demo" />
      </div>
    </ShowcaseCard>
  )
}

function PageHeaderShowcase() {
  return (
    <ShowcaseCard
      id="page-header"
      title="PageHeader"
      description="The public site's primary navigation bar — sticky, responsive, with a desktop mega-menu drawer that slides down on hover for items with sub-pages, and a mobile accordion fallback."
    >
      <p className="text-xs text-muted-foreground">
        Live component — hover Films, Company, Services, or ODDFEST to trigger the flyout drawer.
      </p>
      <div className="overflow-hidden rounded-md border">
        <PublicNavbar />
      </div>
    </ShowcaseCard>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminUIShowcasePage() {
  return (
    <div className="flex gap-8 items-start">
      {/* Sticky sidebar */}
      <aside className="sticky top-4 w-44 shrink-0 self-start">
        <p className="mb-4 text-lg font-semibold">Components</p>
        {NAV.map((group) => (
          <div key={group.tier} className="mb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.tier}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollTo(item.id)}
                    className="w-full rounded px-2 py-1 text-left text-sm text-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </aside>

      {/* Main content */}
      <main className="min-w-0 flex-1 pb-16">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">UI Showcase</h1>
          <p className="mt-2 text-muted-foreground">
            Every standardized component, live and interactive. Atoms → Molecules → Organisms.
          </p>
        </div>

        {/* Foundation */}
        <div className="space-y-8">
          <h2 className="border-b pb-2 text-xl font-semibold tracking-tight">Foundation</h2>
          <ColorGuideShowcase />
        </div>

        {/* Atoms */}
        <div className="mt-12 space-y-8">
          <h2 className="border-b pb-2 text-xl font-semibold tracking-tight">Atoms</h2>
          <ButtonShowcase />
          <InputShowcase />
          <LabelShowcase />
          <BadgeShowcase />
          <SeparatorShowcase />
          <SkeletonShowcase />
          <AvatarShowcase />
          <TextareaShowcase />
          <BrandLoadingBarShowcase />
        </div>

        {/* Molecules */}
        <div className="mt-12 space-y-8">
          <h2 className="border-b pb-2 text-xl font-semibold tracking-tight">Molecules</h2>
          <CardShowcase />
          <AlertShowcase />
          <SelectShowcase />
          <TabsShowcase />
          <DialogShowcase />
          <UserChipShowcase />
          <MultiButtonShowcase />
          <CompactMultiButtonShowcase />
          <SearchMultiSelectShowcase />
          <DiagonalLinesShowcase />
        </div>

        {/* Organisms */}
        <div className="mt-12 space-y-8">
          <h2 className="border-b pb-2 text-xl font-semibold tracking-tight">Organisms</h2>
          <TableShowcase />
          <FormShowcase />
          <LoadingSpinnerShowcase />
          <YoutubeEmbedShowcase />
          <PageHeaderShowcase />
        </div>
      </main>
    </div>
  )
}
